import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PageLayout from '@/components/shared/PageLayout';
import { useAuth } from '@/contexts/AuthContext';
import { useClient } from '@/context/client-context';
import { useDataWithLoading } from '@/hooks/use-drill-data';
import { Drill, TagPreference } from '@/components/drill-library/types/drill-types';
import { Program } from './types/program-types';
import { createProgram, updateProgram, deleteProgram } from './services/programs-api';
import { clientDisplayLabel } from '@/lib/clients/roster-fields';
import { useToast } from '@/hooks/use-toast';
import { downloadProgram, uploadProgram } from '@/utils/programExport';
import ProgramBuilder from '@/components/drill-planner/ProgramBuilder';
import ProgramHistory from '@/components/drill-planner/ProgramHistory';
import DrillPickerModal from '@/components/drill-planner/DrillPickerModal';
import Modal from '@/components/shared/Modal';
import { Download, Upload, Save, Plus, UserPlus, PersonStanding, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ContextualToolbar, ToolbarButtonGroup, ToolbarSeparator } from '@/components/ui/contextual-toolbar';
import { SelectClientDialog } from '@/components/select-client-dialog';
import { Link } from 'react-router-dom';
import { DEFAULT_SLOT_LABELS } from './lib/program-structure';
import { usePlan } from '@/context/plan-context';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import DrillPlanCreator from './DrillPlanCreator';
import MobileDrillPlanWizard from './MobileDrillPlanWizard';
import type { SuggestDrillsState } from '@/components/drill-library/SuggestDrillsButton';

const DrillPlannerPage: React.FC = () => {
    const { user } = useAuth();
    const { client: selectedClient } = useClient();
    const { toast } = useToast();
    const { programs, programsLoading, activePlan, setActivePlanId } = usePlan();
    const location = useLocation();
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    // Detect "create mode" — navigated from "Suggest Drills" button or ?mode=create
    const params = new URLSearchParams(location.search);
    const isCreateMode = params.get('mode') === 'create';
    const suggestState = location.state as SuggestDrillsState | null;

    const [hasAutoCreated, setHasAutoCreated] = useState(false);

    // Track component lifecycle
    useEffect(() => {
        return () => {
            // Cleanup on unmount
        };
    }, []);

    // Data hooks
    const { data: drills, loading: drillsLoading } = useDataWithLoading<Drill>('drills');
    
    // State
    const [currentProgram, setCurrentProgram] = useState<Program | null>(activePlan ?? null);
    const [lastSavedSnapshot, setLastSavedSnapshot] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [programToDelete, setProgramToDelete] = useState<Program | null>(null);
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [activeSlot, setActiveSlot] = useState<{ dayIndex: number; slotKey: string } | null>(null);
    const [tagPreferences, setTagPreferences] = useState<{ [key: string]: TagPreference }>({});
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const currentProgramSnapshot = useMemo(
        () => (currentProgram ? JSON.stringify(currentProgram) : null),
        [currentProgram]
    );
    const hasUnsavedChanges = useMemo(() => {
        if (!currentProgram) return false;
        if (currentProgram.id.startsWith('new-')) return true;
        if (!lastSavedSnapshot) return false;
        return currentProgramSnapshot !== lastSavedSnapshot;
    }, [currentProgram, currentProgramSnapshot, lastSavedSnapshot]);

    // Load tag preferences from localStorage
    useEffect(() => {
        try {
            const storedPrefs = localStorage.getItem('tagPreferences');
            if (storedPrefs) {
                setTagPreferences(JSON.parse(storedPrefs));
            }
        } catch (e) {
            logger.error("Failed to load tag preferences from localStorage", e);
        }
    }, []);

    const createNewProgram = useCallback(async () => {
        if (!selectedClient) return;
        
        const newProgram: Program = {
            id: `new-${Date.now()}`,
            name: `${clientDisplayLabel(selectedClient)} – Program #${programs.length + 1}`,
            description: '',
            days: [
                {
                    name: 'Day 1',
                    slots: {}
                }
            ],
            slotLabels: { ...DEFAULT_SLOT_LABELS },
            clientId: selectedClient.id,
            clientName: clientDisplayLabel(selectedClient),
            createdBy: user?.uid || 'anonymous',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };
        
        // On mobile, save the program immediately and navigate to library
        if (isMobile) {
            try {
                setIsSaving(true);
                const { id, createdAt, updatedAt, ...dataToSave } = newProgram;
                const newId = await createProgram(dataToSave);
                const savedProgram = { ...newProgram, id: newId, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
                setActivePlanId(newId);
                setLastSavedSnapshot(JSON.stringify(savedProgram));
                toast({
                    title: "Success",
                    description: `Program "${savedProgram.name}" created!`
                });
                // Navigate to library after creating plan
                navigate('/drills');
            } catch (error) {
                logger.error('Error creating program:', error);
                toast({
                    title: "Error",
                    description: error instanceof Error ? error.message : "Failed to create program",
                    variant: "destructive"
                });
            } finally {
                setIsSaving(false);
            }
        } else {
            // On desktop, just set it as current program (existing behavior)
        setCurrentProgram(newProgram);
        setLastSavedSnapshot(null);
        }
    }, [selectedClient, programs.length, user?.uid, isMobile, navigate, setActivePlanId, toast]);

    useEffect(() => {
        if (!selectedClient) {
            setCurrentProgram(null);
            setLastSavedSnapshot(null);
            setHasAutoCreated(false);
            return;
        }

        if (activePlan) {
            if (!hasUnsavedChanges) {
                setCurrentProgram((prev) => {
                    if (!prev || prev.id !== activePlan.id) {
                        return activePlan;
                    }
                    return prev;
                });
                const snapshot = JSON.stringify(activePlan);
                setLastSavedSnapshot((prev) => (prev === snapshot ? prev : snapshot));
                setHasAutoCreated(false);
            }
            return;
        }

        // Auto-select the first program if there's only one and no active plan
        if (programs.length === 1 && !currentProgram && !hasAutoCreated) {
            const firstProgram = programs[0];
            setActivePlanId(firstProgram.id);
            setCurrentProgram(firstProgram);
            setLastSavedSnapshot(JSON.stringify(firstProgram));
            return;
        }

        const params = new URLSearchParams(location.search);
        if (!hasAutoCreated && params.get('newPlan') === '1') {
            createNewProgram();
            setHasAutoCreated(true);
        }
    }, [selectedClient, activePlan, location.search, hasAutoCreated, programs, setActivePlanId, createNewProgram, hasUnsavedChanges, currentProgram]);

    const handleUpdateProgram = (updater: (draft: Program) => void) => {
        if (currentProgram) {
            const updatedProgram = { ...currentProgram };
            updater(updatedProgram);
            if (selectedClient) {
                updatedProgram.clientId = selectedClient.id;
                updatedProgram.clientName = clientDisplayLabel(selectedClient);
            }
            setCurrentProgram(updatedProgram);
        }
    };

    const handleSaveProgram = async () => {
        if (!currentProgram) {
            toast({
                title: "Error",
                description: "Cannot save program: missing data",
                variant: "destructive"
            });
            return;
        }

        if (!selectedClient) {
            toast({
                title: "Error",
                description: "Please select a client before saving a program",
                variant: "destructive"
            });
            return;
        }
        
        setIsSaving(true);
        try {
            const isNew = currentProgram.id.startsWith('new-');
            const { id, createdAt, updatedAt, ...dataToSave } = currentProgram;

            // Ensure clientId is always set
            if (!dataToSave.clientId) {
                dataToSave.clientId = selectedClient.id;
                dataToSave.clientName = clientDisplayLabel(selectedClient);
            }
            
            if (isNew) {
                const newId = await createProgram(dataToSave);
                const savedProgram = { ...currentProgram, id: newId, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
                setCurrentProgram(savedProgram);
                setActivePlanId(newId);
                setLastSavedSnapshot(JSON.stringify(savedProgram));
                toast({
                    title: "Success",
                    description: `Program "${savedProgram.name}" saved successfully!`
                });
            } else {
                await updateProgram(currentProgram.id, dataToSave);
                const updatedProgramWithTimestamp = { ...currentProgram, updatedAt: new Date().toISOString() };
                setCurrentProgram(updatedProgramWithTimestamp);
                setActivePlanId(updatedProgramWithTimestamp.id);
                setLastSavedSnapshot(JSON.stringify(updatedProgramWithTimestamp));
                toast({
                    title: "Success",
                    description: `Program "${updatedProgramWithTimestamp.name}" updated successfully!`
                });
            }
            
        } catch (error) {
            logger.error('Error saving program:', error);
            toast({
                title: "Error",
                description: error instanceof Error ? error.message : "Failed to save program",
                variant: "destructive"
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteProgram = async () => {
        if (!programToDelete) return;

        try {
            await deleteProgram(programToDelete.id);
            toast({
                title: "Success",
                description: "Program deleted successfully!"
            });
            setProgramToDelete(null);
            
            // If we deleted the current program, create a new one
            if (currentProgram?.id === programToDelete.id) {
                setActivePlanId(null);
                setCurrentProgram(null);
                setLastSavedSnapshot(null);
            }
        } catch (error) {
            logger.error('Error deleting program:', error);
            toast({
                title: "Error",
                description: "Failed to delete program",
                variant: "destructive"
            });
        }
    };

    const handleSelectDrill = (drillId: string) => {
        if (!currentProgram) return;
        // Determine target slot: active one or first available
        let dayIndex = activeSlot?.dayIndex ?? 0;
        let slotKey = activeSlot?.slotKey;
        if (!slotKey) {
            const defaultOrder = ['A1','A2','B1','B2','C1','C2'];
            const day = currentProgram.days[dayIndex];
            const existing = day.slots || {};
            slotKey = defaultOrder.find(k => !existing[k]) || defaultOrder[0];
        }

        handleUpdateProgram(draft => {
            if (!draft.days[dayIndex]) return;
            draft.days[dayIndex].slots = {
                ...draft.days[dayIndex].slots,
                [slotKey!]: {
                    drillId,
                    sets: '',
                    reps: '',
                    rest: '',
                    tempo: '',
                    notes: ''
                }
            };
        });
        
        setIsPickerOpen(false);
        setActiveSlot(null);
    };

    const handleExportProgram = () => {
        if (!currentProgram) return;
        
        try {
            downloadProgram(currentProgram);
            toast({
                title: "Success",
                description: "Program exported successfully!"
            });
        } catch (error) {
            logger.error('Error exporting program:', error);
            toast({
                title: "Error",
                description: "Failed to export program",
                variant: "destructive"
            });
        }
    };

    const handleImportProgram = async () => {
        try {
            const importedProgram = await uploadProgram();
            if (importedProgram) {
                const newProgram: Program = {
                    ...importedProgram,
                    id: `new-${Date.now()}`,
                    createdBy: user?.uid || 'anonymous',
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                } as Program;
                
                setCurrentProgram(newProgram);
                setLastSavedSnapshot(null);
                toast({
                    title: "Success",
                    description: "Program imported successfully!"
                });
            }
        } catch (error) {
            logger.error('Error importing program:', error);
            toast({
                title: "Error",
                description: "Failed to import program",
                variant: "destructive"
            });
        }
    };

    if (programsLoading || drillsLoading) {
        return (
            <PageLayout
              title="Loading..."
              isLoading={true}
              titleBadge="Beta"
            >
                <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
                        <p className="text-muted">Loading drill planner...</p>
                    </div>
                </div>
            </PageLayout>
        );
    }

    // Mobile: always show the dedicated wizard instead of the legacy desktop ProgramBuilder
    if (isMobile) {
        return (
            <PageLayout
              title="Drill Plan"
              subtitle={selectedClient ? undefined : 'Select a client to begin'}
              titleBadge="Beta"
              compactContent
            >
                <MobileDrillPlanWizard
                    drills={drills ?? []}
                    initialFilters={suggestState?.filters ?? undefined}
                    onSaved={() => navigate('/drill-planner')}
                />
            </PageLayout>
        );
    }

    // Desktop create mode: template-driven DrillPlanCreator
    if (isCreateMode) {
        return (
            <PageLayout
              title="New Drill Plan"
              subtitle="Pick a template and auto-fill from client analysis"
              titleBadge="Beta"
              headerActions={
                <Button variant="ghost" size="sm" onClick={() => navigate('/drill-planner')} className="h-9 text-xs">
                    ← Back
                </Button>
              }
            >
                <DrillPlanCreator
                    drills={drills ?? []}
                    initialFilters={suggestState?.filters ?? undefined}
                    onSaved={() => navigate('/drill-planner')}
                />
            </PageLayout>
        );
    }

    if (!selectedClient) {
        return (
            <PageLayout
              title="Intervention Plan"
              subtitle="Create and manage customized exercise programs with AI-powered drill recommendations"
              titleBadge="Beta"
            >
                <div className="flex flex-col gap-8 py-4">
                    <div className="flex items-start">
                        <Card className="max-w-lg w-full">
                            <CardHeader>
                                <div className="bg-accent/10 text-accent p-3 rounded-full mb-4 w-fit">
                                    <PersonStanding className="h-8 w-8" />
                                </div>
                                <CardTitle>Select a Client to Begin</CardTitle>
                                <CardDescription>
                                    To start a new Intervention Plan, select an existing client or create a new one.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <SelectClientDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                                        <Button className="h-12" onClick={() => setIsDialogOpen(true)}>
                                            <UserPlus className="mr-2 h-4 w-4" />
                                            Select Existing Client
                                        </Button>
                                    </SelectClientDialog>
                                    <Button asChild variant="secondary" className="h-12">
                                        <Link to="/clients/new">
                                            Create New Client
                                        </Link>
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </PageLayout>
        );
    }

    if (!currentProgram && selectedClient) {
        return (
            <PageLayout
              title="Choose an Intervention Plan"
              subtitle={`Select an existing program or create a new one for ${clientDisplayLabel(selectedClient)}`}
              titleBadge="Beta"
              headerActions={
                programs.length > 0 && (
                    <ProgramHistory
                        programs={programs}
                        activeId={null}
                        onLoad={(program) => {
                            setActivePlanId(program.id);
                            setCurrentProgram(program);
                            setLastSavedSnapshot(JSON.stringify(program));
                        }}
                        onNew={createNewProgram}
                        onDelete={setProgramToDelete}
                    />
                )
              }
            >
                <div className="flex flex-col items-center justify-center py-8 text-center space-y-4">
                    {programs.length === 0 ? (
                        <>
                            <p className="text-muted mb-2">
                                No intervention plans found for {clientDisplayLabel(selectedClient)}.
                            </p>
                            <p className="text-sm text-muted mb-6">
                                Create your first plan to get started.
                            </p>
                        </>
                    ) : (
                        <>
                            <p className="text-muted mb-2">
                                {programs.length} plan{programs.length !== 1 ? 's' : ''} found for {clientDisplayLabel(selectedClient)}.
                            </p>
                            <p className="text-sm text-muted mb-6">
                                Select a plan from the dropdown above or create a new one.
                            </p>
                            <div className="text-sm text-left bg-fg/5 rounded-lg p-4 max-w-md w-full">
                                <p className="font-medium mb-3">Available Plans:</p>
                                <div className="space-y-2">
                                    {programs.map(program => (
                                        <button
                                            key={program.id}
                                            onClick={() => {
                                                setActivePlanId(program.id);
                                                setCurrentProgram(program);
                                                setLastSavedSnapshot(JSON.stringify(program));
                                            }}
                                            className="w-full text-left p-3 rounded-lg border hover:bg-accent hover:border-accent transition-colors"
                                        >
                                            <div className="font-medium">{program.name}</div>
                                            {program.description && (
                                                <div className="text-xs text-muted mt-1">{program.description}</div>
                                            )}
                                            <div className="text-xs text-muted mt-1">
                                                {program.days.length} day{program.days.length !== 1 ? 's' : ''}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                    <div className="flex flex-col sm:flex-row gap-3 mt-6">
                        <Button 
                            onClick={createNewProgram} 
                            className="gap-2"
                            disabled={isSaving}
                        >
                            <Plus className="h-4 w-4" />
                            {isSaving ? 'Creating...' : 'Start New Plan'}
                        </Button>
                        <Button
                            variant="outline"
                            className="gap-2"
                            onClick={() => navigate('/drill-planner?mode=create')}
                        >
                            <Wand2 className="h-4 w-4" />
                            Create with Template
                        </Button>
                    </div>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout
          title="Intervention Plan"
          subtitle="Create and manage customized exercise programs with AI-powered drill recommendations"
          titleBadge="Beta"
        >
            <div className="h-full flex flex-col overflow-hidden -mx-4 sm:-mx-6 md:-mx-8 lg:-mx-10 md:ml-0">
                {/* Main Content — visible on all screen sizes */}
                <div className="flex flex-col h-full overflow-hidden">
                    {/* Contextual Toolbar - Inline */}
                    <ContextualToolbar position="top">
                        <ToolbarButtonGroup align="right">
                            <Button 
                                onClick={handleSaveProgram}
                                disabled={!hasUnsavedChanges || isSaving}
                                size="sm"
                                className="h-10"
                            >
                                <Save className="h-4 w-4 sm:mr-2" />
                                <span className="hidden sm:inline">{isSaving ? 'Saving...' : 'Save'}</span>
                            </Button>
                            
                            <ToolbarSeparator />
                            
                            <Button 
                                onClick={createNewProgram} 
                                variant="outline" 
                                size="sm" 
                                className="h-10"
                            >
                                <Plus className="h-4 w-4 sm:mr-2" />
                                <span className="hidden sm:inline">New</span>
                            </Button>
                            <Button 
                                onClick={handleImportProgram} 
                                variant="outline" 
                                size="sm" 
                                className="h-10"
                                title="Import Program"
                            >
                                <Upload className="h-4 w-4 sm:mr-2" />
                                <span className="hidden sm:inline">Import</span>
                            </Button>
                            <Button 
                                onClick={handleExportProgram} 
                                disabled={!currentProgram}
                                variant="outline"
                                size="sm"
                                className="h-10"
                                title="Export Program"
                            >
                                <Download className="h-4 w-4 sm:mr-2" />
                                <span className="hidden sm:inline">Export</span>
                            </Button>
                            
                            <ToolbarSeparator />
                            
                            {/* Program History Dropdown */}
                            <ProgramHistory
                                programs={programs}
                                activeId={currentProgram?.id || null}
                                onLoad={(program) => {
                                    setActivePlanId(program.id);
                                    setCurrentProgram(program);
                                    setLastSavedSnapshot(JSON.stringify(program));
                                }}
                                onNew={createNewProgram}
                                onDelete={setProgramToDelete}
                            />
                        </ToolbarButtonGroup>
                    </ContextualToolbar>


                    {/* Program Builder - Takes remaining height */}
                    {currentProgram && (
                    <div className="flex-1 min-h-0 overflow-auto">
                            <ProgramBuilder
                                program={currentProgram}
                                onUpdate={handleUpdateProgram}
                                drills={drills}
                                onOpenDrillPicker={(dayIndex, slotId) => {
                                    // Store the real slot key (e.g., 'A1') so add writes to correct slot
                                    const key = typeof slotId === 'string' ? slotId : String(slotId);
                                    setActiveSlot({ dayIndex, slotKey: key });
                                    setIsPickerOpen(true);
                                }}
                                tagPreferences={tagPreferences}
                            />
                        </div>
                    )}
                </div>

                {/* Modals */}
                <DrillPickerModal
                    isOpen={isPickerOpen}
                    onClose={() => setIsPickerOpen(false)}
                    drills={drills}
                    onSelect={handleSelectDrill}
                />

                <Modal isOpen={!!programToDelete} onClose={() => setProgramToDelete(null)} title="Confirm Deletion">
                    <p>Are you sure you want to delete the program "{programToDelete?.name}"? This action cannot be undone.</p>
                    <div className="flex justify-end gap-3 mt-4">
                        <Button variant="outline" onClick={() => setProgramToDelete(null)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDeleteProgram}>
                            Delete
                        </Button>
                    </div>
                </Modal>
            </div>
        </PageLayout>
    );
};
import logger from '@/lib/logging';

export default DrillPlannerPage;

